import { type PropsWithChildren, type ReactElement } from 'react';

import { ChartSeriesActionContext } from '../../core/components/series-action/ChartSeriesActionContext.js';
import { ChartLegendContext } from '../context/ChartLegendContext.js';
import { FormatterContext } from '../context/FormatterContext.js';
import { LabelsContext } from '../context/LabelsContext.js';
import { PieChartConfigContext } from '../context/PieChartConfigContext.js';
import { PieChartRawDataContext } from '../context/PieChartRawDataContext.js';
import {
  PieChartTooltipContext,
  DEFAULT_TOOLTIP_CONFIG,
} from '../context/PieChartTooltipContext.js';
import { ToolbarContext } from '../context/ToolbarContext.js';
import type {
  PieChartData,
  PieDonutConfig,
} from '../types/pie-chart.config.js';
import { StoreProvider } from './StoreProvider.js';

export interface ChartProvidersProps extends PropsWithChildren {
  // oxlint-disable-next-line typescript/no-deprecated
  chartData: PieChartData;
  chartConfig: PieDonutConfig;
}

export const ChartProviders = (props: ChartProvidersProps) => {
  const { chartConfig, chartData, children } = props;

  return (
    <ChartSeriesActionContext.Provider
      value={{
        // We need a cast here as the provider expects unknown and the
        // react context will not be able to carry the generic anyways.
        chartSeriesActions: chartConfig.seriesActions as (
          series: unknown,
        ) => ReactElement<PropsWithChildren>,
      }}
    >
      <PieChartRawDataContext.Provider value={chartData}>
        <PieChartConfigContext.Provider value={chartConfig}>
          <PieChartTooltipContext.Provider
            value={chartConfig.tooltip ?? DEFAULT_TOOLTIP_CONFIG}
          >
            <LabelsContext.Provider
              value={{
                hidden: chartConfig.labels?.hidden,
                valueType: chartConfig.labels?.valueType,
              }}
            >
              <FormatterContext.Provider
                value={{
                  formatter: chartConfig.formatter,
                  truncationMode: chartConfig.truncationMode,
                }}
              >
                <ChartLegendContext.Provider value={chartConfig.legend}>
                  <ToolbarContext.Provider value={chartConfig.toolbar}>
                    <StoreProvider>{children}</StoreProvider>
                  </ToolbarContext.Provider>
                </ChartLegendContext.Provider>
              </FormatterContext.Provider>
            </LabelsContext.Provider>
          </PieChartTooltipContext.Provider>
        </PieChartConfigContext.Provider>
      </PieChartRawDataContext.Provider>
    </ChartSeriesActionContext.Provider>
  );
};
